import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import axiosInstance from "@/lib/axios";
import { checkInEndpoint } from "@/config/endpoints";
import { markManualCheckOut } from "../components/checkInSession";

export type PresenceStatus = "checked_in" | "on_break" | "checked_out";

/** GET /core/check-in/me — the signed-in person's own presence. */
export interface CheckInState {
  /** The feature is on for the org AND applies to this person's role. */
  applies: boolean;
  enabled: boolean;
  /** checked in / on a break / checked out (a break still counts as checked in). */
  presence: PresenceStatus;
  checkedIn: boolean;
  checkedInAt: string | null;
  lastActivityAt: string | null;
  inactivityMinutes: number | null;
  autoCheckoutAt: string | null;
  /**
   * Only in a "Switch user" session: what the Super Admin behind it may do
   * FOR the person being viewed. Absent everywhere else (older servers too).
   */
  onBehalf: { canCheckIn: boolean; canCheckOut: boolean } | null;
}

export const CHECK_IN_STATE_KEY = ["check-in", "me"] as const;
export const CHECK_IN_ROLES_KEY = ["check-in", "roles"] as const;

export function parseCheckInState(raw: any): CheckInState {
  const minutes = Number(raw?.inactivityMinutes);
  const checkedIn = raw?.checkedIn === true;
  return {
    applies: raw?.applies === true,
    enabled: raw?.enabled === true,
    presence:
      raw?.presence === "on_break" || raw?.presence === "checked_in" || raw?.presence === "checked_out"
        ? raw.presence
        : checkedIn
          ? "checked_in"
          : "checked_out",
    checkedIn,
    checkedInAt: typeof raw?.checkedInAt === "string" ? raw.checkedInAt : null,
    lastActivityAt:
      typeof raw?.lastActivityAt === "string" ? raw.lastActivityAt : null,
    inactivityMinutes:
      raw?.inactivityMinutes != null && Number.isFinite(minutes) && minutes > 0
        ? minutes
        : null,
    autoCheckoutAt:
      typeof raw?.autoCheckoutAt === "string" && raw.autoCheckoutAt
        ? raw.autoCheckoutAt
        : null,
    onBehalf:
      raw?.onBehalf && typeof raw.onBehalf === "object"
        ? {
            canCheckIn: raw.onBehalf.canCheckIn === true,
            canCheckOut: raw.onBehalf.canCheckOut === true,
          }
        : null,
  };
}

/**
 * One small read per session (and on every `checkin:status` push). Off for
 * a "Switch user" session: presence is the person's own, not the viewer's.
 */
export function useCheckInState(enabled = true) {
  return useQuery<CheckInState>({
    queryKey: CHECK_IN_STATE_KEY,
    queryFn: async () => {
      const res = await axiosInstance.get(`${checkInEndpoint}/me`);
      return parseCheckInState(res.data);
    },
    enabled,
    // Short on purpose: the endpoint is one PK read, and a tab coming back
    // must learn quickly that the sweep checked it out while it was hidden.
    staleTime: 15_000,
    // The global default is off; presence is the one query that should
    // refresh whenever the tab regains focus.
    refetchOnWindowFocus: true,
  });
}

export function useCheckInMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<CheckInState> => {
      const res = await axiosInstance.post(`${checkInEndpoint}/in`, { source: "web" });
      return parseCheckInState(res.data);
    },
    onSuccess: (state) => {
      qc.setQueryData(CHECK_IN_STATE_KEY, state);
      toast.success("You're checked in", { id: "check-in" });
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Couldn't check you in", { id: "check-in" });
    },
  });
}

export function useCheckOutMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<CheckInState> => {
      const res = await axiosInstance.post(`${checkInEndpoint}/out`);
      return parseCheckInState(res.data);
    },
    onSuccess: (state) => {
      // Their own choice — the prompt must not come straight back.
      markManualCheckOut();
      qc.setQueryData(CHECK_IN_STATE_KEY, state);
      toast.success("You're checked out", { id: "check-in" });
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Couldn't check you out", { id: "check-in" });
    },
  });
}

/** "I'm still here" — feeds the inactivity clock. Never toasts. */
export async function sendCheckInHeartbeat(): Promise<CheckInState | null> {
  try {
    const res = await axiosInstance.post(`${checkInEndpoint}/heartbeat`);
    return parseCheckInState(res.data);
  } catch {
    return null;
  }
}

export interface CheckInRole {
  id: number;
  name: string;
  hierarchy: number;
  users: number;
}

/** Settings card: the roles the feature applies to (personal-scope only). */
export function useCheckInRoles(enabled = true) {
  return useQuery<CheckInRole[]>({
    queryKey: CHECK_IN_ROLES_KEY,
    queryFn: async () => {
      const res = await axiosInstance.get(`${checkInEndpoint}/roles`);
      const rows = res.data?.roles;
      return Array.isArray(rows) ? (rows as CheckInRole[]) : [];
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}

// ─── On behalf of someone (Super Admin) + the team board ────────────────────

export const CHECK_IN_TEAM_KEY = ["check-in", "team"] as const;
export const CHECK_IN_SETTINGS_KEY = ["check-in", "settings"] as const;
export const CHECK_IN_HOLDS_KEY = ["check-in", "holds"] as const;

/**
 * "Check in for them" / "Check out for them". Used from the team board and
 * from the header pill while a Super Admin is viewing as the person. The
 * server re-checks everything (Super Admin, the org's opt-in, working hours,
 * location rule, same tenant).
 */
export function useCheckInForMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { userId: number; name?: string | null }) => {
      const res = await axiosInstance.post(`${checkInEndpoint}/team/${args.userId}/in`);
      return res.data as { checkedIn: boolean; alreadyIn: boolean };
    },
    onSuccess: (data, args) => {
      void qc.invalidateQueries({ queryKey: CHECK_IN_STATE_KEY });
      void qc.invalidateQueries({ queryKey: CHECK_IN_TEAM_KEY });
      const who = args.name ?? "They";
      toast.success(data?.alreadyIn ? `${who} is already checked in` : `${who} is checked in`, { id: "check-in-for" });
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Couldn't check them in", { id: "check-in-for" });
    },
  });
}

export function useCheckOutForMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { userId: number; name?: string | null }) => {
      const res = await axiosInstance.post(`${checkInEndpoint}/team/${args.userId}/out`);
      return res.data as { checkedOut: boolean };
    },
    onSuccess: (_data, args) => {
      void qc.invalidateQueries({ queryKey: CHECK_IN_STATE_KEY });
      void qc.invalidateQueries({ queryKey: CHECK_IN_TEAM_KEY });
      toast.success(`${args.name ?? "They"} ${args.name ? "is" : "are"} checked out`, { id: "check-in-for" });
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Couldn't check them out", { id: "check-in-for" });
    },
  });
}

export interface TeamMember {
  id: number;
  name: string;
  email: string | null;
  roleName: string | null;
  branches: string[];
  status: PresenceStatus;
  overdue: boolean;
  checkedInAt: string | null;
  presenceAt: string | null;
  lastActivityAt: string | null;
  onBreak: { reason: string; since: string } | null;
  source: string | null;
  place: string | null;
  ip: string | null;
  lastCheckOut: { reason: string; at: string } | null;
  today: {
    checkedInMinutes: number;
    breakMinutes: number;
    workMinutes: number;
    firstInAt: string | null;
    sessions: number;
    lateMinutes: number | null;
  };
}

export interface TeamBoard {
  enabled: boolean;
  timezone: string;
  generatedAt: string;
  canManage: boolean;
  canCheckIn: boolean;
  locationRequired: boolean;
  counts: { total: number; checkedIn: number; onBreak: number; checkedOut: number };
  members: TeamMember[];
}

/** GET /core/check-in/team — the live board (admins: everyone; managers: their team). */
export function useCheckInTeam(enabled = true) {
  return useQuery<TeamBoard>({
    queryKey: CHECK_IN_TEAM_KEY,
    queryFn: async () => {
      const res = await axiosInstance.get(`${checkInEndpoint}/team`);
      const d = res.data ?? {};
      return {
        enabled: d.enabled === true,
        timezone: typeof d.timezone === "string" ? d.timezone : "Asia/Kolkata",
        generatedAt: d.generatedAt ?? new Date().toISOString(),
        canManage: d.canManage === true,
        canCheckIn: d.canCheckIn === true,
        locationRequired: d.locationRequired === true,
        counts: d.counts ?? { total: 0, checkedIn: 0, onBreak: 0, checkedOut: 0 },
        members: Array.isArray(d.members) ? (d.members as TeamMember[]) : [],
      };
    },
    enabled,
    staleTime: 15_000,
    // A live board: re-read every 30 s while open (sockets nudge it sooner).
    refetchInterval: enabled ? 30_000 : false,
    refetchOnWindowFocus: true,
  });
}

/**
 * The backlog ("hold queue") settings. When every counsellor is checked out,
 * an automated assignment is PARKED instead of failing, and released as people
 * check in — under a fair-share ceiling so the first person in at 10am does not
 * inherit the whole night.
 */
export interface CheckInBacklogSlice {
  enabled: boolean;
  perTickReleases: number;
  maxHoldHours: number;
  maxAttempts: number;
}

/** The v2 org policy — only the keys the CRM edits today. */
export interface CheckInPolicySlice {
  allowAdminCheckIn: boolean;
  /** Automations hand out leads to everyone, checked in or not (off by default). */
  automationsIgnoreCheckIn: boolean;
  backlog: CheckInBacklogSlice;
}

const BACKLOG_FALLBACK: CheckInBacklogSlice = {
  enabled: true,
  perTickReleases: 10,
  maxHoldHours: 2,
  maxAttempts: 5,
};

/** GET /core/check-in/settings (Super Admin). */
export function useCheckInSettings(enabled = true) {
  return useQuery<{ enabled: boolean; policy: CheckInPolicySlice }>({
    queryKey: CHECK_IN_SETTINGS_KEY,
    queryFn: async () => {
      const res = await axiosInstance.get(`${checkInEndpoint}/settings`);
      const st = res.data?.settings ?? {};
      return {
        enabled: st.enabled === true,
        policy: {
          allowAdminCheckIn: st.policy?.allowAdminCheckIn === true,
          automationsIgnoreCheckIn: st.policy?.automationsIgnoreCheckIn === true,
          // An older API has no `backlog` — fall back to the server's own
          // defaults so the card renders the behaviour that is actually live
          // rather than an empty form.
          backlog: {
            enabled: st.policy?.backlog?.enabled !== false,
            perTickReleases:
              Number(st.policy?.backlog?.perTickReleases) || BACKLOG_FALLBACK.perTickReleases,
            maxHoldHours: Number(st.policy?.backlog?.maxHoldHours) || BACKLOG_FALLBACK.maxHoldHours,
            maxAttempts: Number(st.policy?.backlog?.maxAttempts) || BACKLOG_FALLBACK.maxAttempts,
          },
        },
      };
    },
    enabled,
    staleTime: 60_000,
  });
}

/** PUT /core/check-in/settings — a PARTIAL policy patch (Super Admin). */
export function useUpdateCheckInPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      policy: Partial<Omit<CheckInPolicySlice, "backlog">> & {
        backlog?: Partial<CheckInBacklogSlice>;
      },
    ) => {
      const res = await axiosInstance.put(`${checkInEndpoint}/settings`, { policy });
      return res.data?.settings;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHECK_IN_SETTINGS_KEY });
      void qc.invalidateQueries({ queryKey: CHECK_IN_TEAM_KEY });
      void qc.invalidateQueries({ queryKey: CHECK_IN_STATE_KEY });
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Couldn't save", { id: "check-in-settings" });
    },
  });
}

/**
 * The waiting list: automated assignments parked because everybody who could
 * take them was checked out. This is the one place an operator can see that
 * leads are QUEUED rather than lost.
 */
export interface CheckInHoldSummary {
  waiting: number;
  oldestHeldAt: string | null;
  expired: number;
  releasedToday: number;
  byAutomation: Array<{ automationId: number; name: string; waiting: number }>;
  /**
   * The hard clock: no lead waits longer than this (hours) on the clock —
   * past it, it goes out even if nobody from its pool has checked in. Server
   * constant; 6 when an older API does not send it.
   */
  releaseAfterHours: number;
}

/** GET /core/check-in/holds (Super Admin). */
export function useCheckInHolds(enabled = true) {
  return useQuery<CheckInHoldSummary>({
    queryKey: CHECK_IN_HOLDS_KEY,
    queryFn: async () => {
      const res = await axiosInstance.get(`${checkInEndpoint}/holds`);
      const d = res.data ?? {};
      return {
        waiting: Number(d.waiting) || 0,
        oldestHeldAt: d.oldestHeldAt ?? null,
        expired: Number(d.expired) || 0,
        releasedToday: Number(d.releasedToday) || 0,
        byAutomation: Array.isArray(d.byAutomation) ? d.byAutomation : [],
        releaseAfterHours: Number(d.releaseAfterHours) || 6,
      };
    },
    enabled,
    staleTime: 15_000,
    // The queue drains on its own every minute — keep the number honest while
    // the settings page is open.
    refetchInterval: enabled ? 30_000 : false,
  });
}

/**
 * POST /core/check-in/holds/release — hand the whole waiting list out now,
 * ignoring the fair-share ceiling. A lead whose people are in goes to them; a
 * lead whose pool is all out goes out with check-in ignored, to the rule's own
 * people (it used to refuse with nobody in — owner, 2026-11-02).
 */
export function useReleaseCheckInHolds() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post(`${checkInEndpoint}/holds/release`);
      return res.data as { released: number; cancelled: number; expired: number; stillWaiting: number };
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: CHECK_IN_HOLDS_KEY });
      void qc.invalidateQueries({ queryKey: ["leads"] });
      toast.success(
        data.released > 0
          ? `Released ${data.released} lead${data.released === 1 ? "" : "s"}${
              data.stillWaiting > 0 ? `, ${data.stillWaiting} still waiting` : ""
            }`
          : "Nothing was released — the queue is empty, or every lead was cancelled or gave up",
        { id: "check-in-holds" },
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Couldn't release the waiting list", {
        id: "check-in-holds",
      });
    },
  });
}
